import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Bell, BellOff, Edit, AlertTriangle } from "lucide-react";

const TRIGGER_TYPES = [
  { value: "resource_emptying", label: "Ressourceø tømning anmodet" },
  { value: "maxi_emptying", label: "Maxicontainer tømning anmodet" },
  { value: "zone_cleaning", label: "Rengøringszone aktiveret" },
  { value: "status_change", label: "Statusændring" }
];

const AVAILABLE_ROLES = [
  "admin",
  "heart_leader",
  "chauffør",
  "sweeper",
  "sektionsleder",
  "teamleder"
];

export default function Notifikationer({ user, selectedEvent }) {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [activeTab, setActiveTab] = useState("rules"); // "rules" eller "logs"
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  
  const token = localStorage.getItem("token");
  const eventId = selectedEvent?.id || localStorage.getItem("selectedEventId");
  
  const [formData, setFormData] = useState({
    name: "",
    trigger_type: "resource_emptying",
    trigger_status: "",
    recipient_roles: [],
    recipient_emails: "",
    message_template: ""
  });

  useEffect(() => {
    if (eventId) {
      loadRules();
      loadLogs();
    }
  }, [eventId]);

  const loadRules = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/notification-rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(response.data);
    } catch (error) {
      console.error("Kunne ikke hente notifikationsregler", error);
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/notification-logs?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      console.error("Kunne ikke hente notifikationslog", error);
    }
  };

  const handleCreateRule = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        event_id: eventId,
        recipient_emails: formData.recipient_emails.split(',').map(e => e.trim()).filter(Boolean)
      };
      
      if (editingRule) {
        await axios.put(`${API}/notification-rules/${editingRule.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Regel opdateret");
      } else {
        await axios.post(`${API}/events/${eventId}/notification-rules`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Regel oprettet (DEAKTIVERET som standard)");
      }
      
      setShowCreateForm(false);
      setEditingRule(null);
      resetForm();
      loadRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved oprettelse");
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      await axios.put(`${API}/notification-rules/${rule.id}`, {
        enabled: !rule.enabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(rule.enabled ? "Regel deaktiveret" : "Regel aktiveret");
      loadRules();
    } catch (error) {
      toast.error("Fejl ved opdatering");
    }
  };

  const handleDeleteRule = (rule) => {
    setConfirmDialog({
      open: true,
      title: "Slet notifikationsregel?",
      message: `Er du sikker på du vil slette "${rule.name}"?`,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/notification-rules/${rule.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Regel slettet");
          loadRules();
        } catch (error) {
          toast.error("Fejl ved sletning");
        }
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      trigger_type: rule.trigger_type,
      trigger_status: rule.trigger_status || "",
      recipient_roles: rule.recipient_roles || [],
      recipient_emails: (rule.recipient_emails || []).join(', '),
      message_template: rule.message_template || ""
    });
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      trigger_type: "resource_emptying",
      trigger_status: "",
      recipient_roles: [],
      recipient_emails: "",
      message_template: ""
    });
  };

  const toggleRole = (role) => {
    if (formData.recipient_roles.includes(role)) {
      setFormData({
        ...formData,
        recipient_roles: formData.recipient_roles.filter(r => r !== role)
      });
    } else {
      setFormData({
        ...formData,
        recipient_roles: [...formData.recipient_roles, role]
      });
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="max-w-4xl mx-auto text-center">
          <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Adgang nægtet</h1>
          <p className="text-muted-foreground">Kun administratorer kan administrere notifikationer.</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tilbage til Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false, title: '', message: '', onConfirm: null })}
      />
      
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-heading uppercase tracking-wide">Notifikationer</h1>
              <p className="text-sm text-muted-foreground">{selectedEvent?.name || 'Vælg event'}</p>
            </div>
          </div>
          
          <Button onClick={() => { setShowCreateForm(true); setEditingRule(null); resetForm(); }}>
            <Plus className="h-4 w-4 mr-2" />
            Ny regel
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {/* Warning banner */}
        <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-500">Notifikationer er IKKE aktiveret</p>
            <p className="text-sm text-muted-foreground">
              Dette er en opsætningsside. Når du aktiverer en regel, vil den kun blive logget - 
              der sendes INGEN rigtige notifikationer før systemet er fuldt konfigureret og deployed.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === "rules" ? "default" : "outline"}
            onClick={() => setActiveTab("rules")}
          >
            <Bell className="h-4 w-4 mr-2" />
            Regler ({rules.length})
          </Button>
          <Button
            variant={activeTab === "logs" ? "default" : "outline"}
            onClick={() => setActiveTab("logs")}
          >
            Logfiler ({logs.length})
          </Button>
        </div>

        {/* Rules Tab */}
        {activeTab === "rules" && (
          <div className="space-y-4">
            {rules.length === 0 ? (
              <div className="text-center py-12 bg-card/30 rounded-lg border border-border">
                <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium mb-2">Ingen notifikationsregler</p>
                <p className="text-muted-foreground mb-4">Opret din første regel for at komme i gang.</p>
                <Button onClick={() => { setShowCreateForm(true); resetForm(); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Opret regel
                </Button>
              </div>
            ) : (
              rules.map(rule => (
                <div
                  key={rule.id}
                  className={`p-4 rounded-lg border ${
                    rule.enabled 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-card/30 border-border'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {rule.enabled ? (
                          <Bell className="h-4 w-4 text-green-500" />
                        ) : (
                          <BellOff className="h-4 w-4 text-muted-foreground" />
                        )}
                        <h3 className="font-bold">{rule.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          rule.enabled ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'
                        }`}>
                          {rule.enabled ? 'AKTIV' : 'INAKTIV'}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        Trigger: {TRIGGER_TYPES.find(t => t.value === rule.trigger_type)?.label || rule.trigger_type}
                        {rule.trigger_status && ` (${rule.trigger_status})`}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Modtagere: {rule.recipient_roles?.join(', ') || 'Ingen roller'} 
                        {rule.recipient_emails?.length > 0 && ` + ${rule.recipient_emails.length} emails`}
                      </p>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleRule(rule)}
                      >
                        {rule.enabled ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditRule(rule)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteRule(rule)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === "logs" && (
          <div className="space-y-2">
            {logs.length === 0 ? (
              <div className="text-center py-12 bg-card/30 rounded-lg border border-border">
                <p className="text-muted-foreground">Ingen notifikationer logget endnu.</p>
              </div>
            ) : (
              logs.map(log => (
                <div key={log.id} className="p-3 bg-card/30 rounded-lg border border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{log.trigger_type}</p>
                      <p className="text-xs text-muted-foreground">{log.message}</p>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        log.status === 'sent' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {log.status}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(log.created_at).toLocaleString('da-DK')}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">
                {editingRule ? 'Rediger regel' : 'Opret notifikationsregel'}
              </h2>
              
              <form onSubmit={handleCreateRule} className="space-y-4">
                <div>
                  <Label>Navn</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="F.eks. Ressourceø tømning til chauffører"
                    required
                  />
                </div>
                
                <div>
                  <Label>Trigger type</Label>
                  <select
                    value={formData.trigger_type}
                    onChange={(e) => setFormData({ ...formData, trigger_type: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {TRIGGER_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label>Modtagerroller</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {AVAILABLE_ROLES.map(role => (
                      <Button
                        key={role}
                        type="button"
                        variant={formData.recipient_roles.includes(role) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleRole(role)}
                      >
                        {role}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label>Modtager emails (kommasepareret)</Label>
                  <Input
                    value={formData.recipient_emails}
                    onChange={(e) => setFormData({ ...formData, recipient_emails: e.target.value })}
                    placeholder="email1@test.dk, email2@test.dk"
                  />
                </div>
                
                <div>
                  <Label>Besked skabelon</Label>
                  <textarea
                    value={formData.message_template}
                    onChange={(e) => setFormData({ ...formData, message_template: e.target.value })}
                    placeholder="Skriv din besked her. Brug {point_name} for punktnavn."
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => { setShowCreateForm(false); setEditingRule(null); }}
                  >
                    Annuller
                  </Button>
                  <Button type="submit" className="flex-1">
                    {editingRule ? 'Gem ændringer' : 'Opret (deaktiveret)'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
